import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { CalendarDays, MapPin, Users, BookOpen, Megaphone, LayoutDashboard, Download, Search, Package, ShieldCheck } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const FILES_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;

const fmtDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" });

function Overview({ user, events, announcements, orders, onPayDues }) {
  const { setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pform, setPform] = useState({
    callsign: user.callsign || "",
    specialty: user.specialty || "",
    phone: user.phone || "",
    directory_visible: user.directory_visible !== false,
  });
  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", pform);
      setUser(data);
      setEditing(false);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const next = events[0];

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser(data);
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <div className="grid lg:grid-cols-12 gap-5" data-testid="member-overview">
      <div className="lg:col-span-4 border border-gold/20 bg-card2/50 p-7">
        <div className="mb-5">
          {user.avatar_path ? (
            <img src={`${FILES_BASE}${user.avatar_path}`} alt={user.name} className="w-20 h-20 object-cover rounded-full border-2 border-gold/50 shadow-[0_0_20px_rgba(212,175,55,0.25)]" data-testid="profile-avatar" />
          ) : (
            <LogoMark className="w-12 h-12 text-gold" />
          )}
        </div>
        <h3 className="font-display text-xl uppercase tracking-wider text-parchment">{user.name}</h3>
        {user.callsign && <p className="font-mono text-xs text-gold tracking-[0.25em] mt-1 uppercase">Callsign: {user.callsign}</p>}
        <div className="mt-5 space-y-2 font-mono text-xs tracking-widest uppercase text-stone-400">
          <div className="flex justify-between border-b border-gold/10 pb-2"><span>Tier</span><span className="text-gold">{user.tier}</span></div>
          <div className="flex justify-between border-b border-gold/10 pb-2"><span>Standing</span><span className="text-gold">Good</span></div>
          <div className="flex justify-between border-b border-gold/10 pb-2"><span>Specialty</span><span className="text-parchment">{user.specialty || "—"}</span></div>
          <div className="flex justify-between border-b border-gold/10 pb-2"><span>Phone</span><span className="text-parchment">{user.phone || "—"}</span></div>
          <div className="flex justify-between"><span>Since</span><span className="text-parchment">{user.joined ? new Date(user.joined).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "—"}</span></div>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/handbook.pdf`}
          target="_blank"
          rel="noreferrer"
          data-testid="member-handbook-link"
          className="mt-3 block text-center font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 hover:text-gold transition-colors"
        >
          Download Member Handbook (PDF)
        </a>
        <button
          data-testid="edit-profile-button"
          onClick={() => setEditing((v) => !v)}
          className="mt-5 w-full border border-gold/30 font-condensed uppercase tracking-widest text-sm py-2.5 hover:border-gold hover:text-gold transition-colors"
        >
          {editing ? "Cancel" : "Edit Profile"}
        </button>
        {editing && (
          <form onSubmit={saveProfile} className="mt-5 space-y-4" data-testid="profile-form">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Profile Photo (max 5MB)</label>
              <input
                data-testid="profile-avatar-input"
                type="file"
                accept="image/*"
                onChange={uploadAvatar}
                className="mt-1 w-full text-xs text-stone-400 file:mr-3 file:py-2 file:px-3 file:border file:border-gold/40 file:bg-transparent file:text-gold file:font-condensed file:uppercase file:tracking-widest file:text-[10px] hover:file:bg-gold hover:file:text-obsidian file:transition-colors file:cursor-pointer"
              />
            </div>
            {[
              ["callsign", "Callsign"],
              ["specialty", "Specialty"],
              ["phone", "Phone"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</label>
                <input
                  data-testid={`profile-${k}-input`}
                  value={pform[k]}
                  onChange={(e) => setPform({ ...pform, [k]: e.target.value })}
                  className="mt-1 w-full bg-obsidian border border-gold/25 text-parchment text-sm px-3 py-2 focus:outline-none focus:border-gold"
                />
              </div>
            ))}
            <label className="flex items-center gap-3 cursor-pointer text-xs text-stone-300" data-testid="profile-visible-toggle">
              <input type="checkbox" checked={pform.directory_visible} onChange={(e) => setPform({ ...pform, directory_visible: e.target.checked })} className="accent-[#D4AF37] w-4 h-4" />
              Show me in the member directory
            </label>
            <button type="submit" data-testid="profile-save-button" disabled={saving} className="w-full gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-widest text-sm font-semibold py-2.5 hover:bg-gold-light transition-colors disabled:opacity-60">
              {saving ? "Saving..." : "Save Profile"}
            </button>
          </form>
        )}
      </div>
      <div className="lg:col-span-8 space-y-5">
        {next && (
          <div className="border border-gold/20 bg-card2/50 p-7" data-testid="overview-next-event">
            <p className="eyebrow mb-3">Next Muster</p>
            <h3 className="font-condensed uppercase tracking-wider text-2xl text-parchment">{next.title}</h3>
            <p className="mt-2 text-sm text-stone-400 flex flex-wrap gap-x-5 gap-y-1">
              <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-gold" />{fmtDate(next.date)}</span>
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-gold" />{next.location}</span>
            </p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="border border-gold/20 bg-card2/50 p-7" data-testid="overview-latest-announcement">
            <p className="eyebrow mb-3">Latest Word</p>
            {announcements[0] ? (
              <>
                <h4 className="font-condensed uppercase tracking-wider text-lg text-parchment">{announcements[0].title}</h4>
                <p className="mt-2 text-sm text-stone-400 leading-relaxed line-clamp-4">{announcements[0].body}</p>
              </>
            ) : <p className="text-sm text-stone-500">No announcements yet.</p>}
          </div>
          <div className="border border-gold/20 bg-card2/50 p-7" data-testid="overview-orders">
            <p className="eyebrow mb-3">Recent Orders</p>
            {orders.length === 0 ? (
              <p className="text-sm text-stone-500">No orders yet. <Link to="/store" className="text-gold underline underline-offset-4">Visit the armory</Link>.</p>
            ) : (
              <ul className="space-y-2 font-mono text-xs text-stone-300">
                {orders.slice(0, 3).map((o) => (
                  <li key={o.id} className="flex justify-between border-b border-gold/10 pb-2">
                    <span className="flex items-center gap-2"><Package className="w-3.5 h-3.5 text-gold" />№ {o.id.slice(-8).toUpperCase()}</span>
                    <span className="text-gold">${o.total.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="border border-crimson/30 bg-card2/50 p-7" data-testid="overview-dues-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-crimson">Membership Dues</p>
            {user.dues_status === "current" && (
              <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold border border-gold/40 px-3 py-1" data-testid="dues-current-badge">
                <ShieldCheck className="w-3.5 h-3.5" /> Standing Current
              </span>
            )}
          </div>
          <p className="text-sm text-stone-400 mt-3">Annual dues keep the range bays reserved and the coffee hot. Secure card checkout via Stripe.</p>
          <div className="grid sm:grid-cols-3 gap-3 mt-5">
            {[{ k: "dues_standard", t: "Standard Member", p: 60 }, { k: "dues_tactical", t: "Tactical Squad", p: 120 }, { k: "dues_benefactor", t: "Benefactor Sponsor", p: 300 }].map((d) => (
              <button
                key={d.k}
                data-testid={`pay-dues-${d.k}`}
                onClick={() => onPayDues(d.k)}
                className={`text-left border px-4 py-3.5 transition-[border-color,box-shadow] duration-300 ${
                  d.k === "dues_benefactor"
                    ? "border-crimson/40 hover:border-crimson hover:shadow-[0_0_15px_rgba(178,34,34,0.3)]"
                    : "border-gold/25 hover:border-gold hover:shadow-[0_0_15px_rgba(212,175,55,0.25)]"
                }`}
              >
                <div className="font-condensed uppercase tracking-wider text-parchment text-sm">{d.t}</div>
                <div className="font-mono text-gold text-sm mt-1">${d.p}/yr</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Resources({ resources }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="resources-grid">
      {resources.map((r, i) => (
        <Reveal key={r.id} delay={(i % 3) * 0.06}>
          <div className="h-full border border-gold/15 bg-card2/50 p-6 flex flex-col hover:border-gold/40 transition-colors duration-300" data-testid={`resource-card-${r.id}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{r.kind}</span>
              <span className="font-mono text-[10px] text-stone-500">{r.meta}</span>
            </div>
            <h3 className="font-condensed uppercase tracking-wider text-xl text-parchment mt-3">{r.title}</h3>
            <p className="mt-2 text-sm text-stone-400 leading-relaxed flex-1">{r.description}</p>
            <button
              data-testid={`resource-download-${r.id}`}
              onClick={() => toast.success(`"${r.title}" download started (demo file)`)}
              className="mt-5 flex items-center justify-center gap-2 border border-gold/40 font-condensed uppercase tracking-widest text-sm py-2.5 hover:bg-gold hover:text-obsidian transition-colors duration-300"
            >
              <Download className="w-4 h-4" /> Download
            </button>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

function EventsTab({ events, userId, onRsvp, onInvite }) {
  return (
    <div className="space-y-3" data-testid="events-list">
      {events.map((e) => {
        const rsvped = e.rsvps.includes(userId);
        return (
          <div key={e.id} className="grid md:grid-cols-12 gap-4 items-center border border-gold/15 bg-card2/50 px-6 py-6 hover:bg-card2/80 transition-colors duration-300" data-testid={`event-card-${e.id}`}>
            <div className="md:col-span-2">
              <div className="font-display text-gold text-2xl leading-none">{new Date(`${e.date}T00:00:00`).getDate()}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 mt-1">
                {new Date(`${e.date}T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
              </div>
            </div>
            <div className="md:col-span-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{e.type}</span>
              <h3 className="font-condensed uppercase tracking-wider text-xl text-parchment mt-1">{e.title}</h3>
              <p className="text-sm text-stone-400 mt-1.5 leading-relaxed">{e.description}</p>
              <p className="text-xs text-stone-500 mt-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gold/70" />{e.location} — {e.time}</p>
            </div>
            <div className="md:col-span-2 font-mono text-xs text-stone-400 tracking-widest uppercase">
              <span data-testid={`event-count-${e.id}`}>{e.rsvps.length}</span> / {e.capacity} attending
            </div>
            <div className="md:col-span-2 md:text-right space-y-2">
              <button
                data-testid={`rsvp-button-${e.id}`}
                onClick={() => onRsvp(e.id)}
                className={`font-condensed uppercase tracking-widest text-sm px-6 py-2.5 transition-colors duration-300 ${
                  rsvped ? "bg-gold text-obsidian font-semibold" : "border border-gold/40 hover:bg-gold hover:text-obsidian"
                }`}
              >
                {rsvped ? "Attending" : "RSVP"}
              </button>
              <button
                data-testid={`invite-guest-${e.id}`}
                onClick={() => onInvite(e.id)}
                className="block md:ml-auto font-condensed uppercase tracking-widest text-xs border border-crimson/40 text-crimson px-4 py-2 hover:bg-crimson hover:text-parchment transition-colors"
              >
                Invite Guest
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CalendarTab({ events }) {
  const [cursor, setCursor] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() }; });
  const byDate = useMemo(() => {
    const map = {};
    events.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [events]);
  const first = new Date(cursor.y, cursor.m, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [...Array(startDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const shift = (n) => setCursor((c) => { const d = new Date(c.y, c.m + n, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const today = new Date();
  const isToday = (d) => d && today.getFullYear() === cursor.y && today.getMonth() === cursor.m && today.getDate() === d;
  const key = (d) => `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <div data-testid="calendar-tab">
      <div className="flex items-center justify-between mb-6">
        <button data-testid="calendar-prev" onClick={() => shift(-1)} className="border border-gold/30 font-condensed uppercase tracking-widest text-sm px-4 py-2 hover:border-gold hover:text-gold transition-colors">&larr; Prev</button>
        <h3 className="font-display text-xl sm:text-2xl uppercase tracking-wider text-parchment" data-testid="calendar-month">
          {first.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        </h3>
        <button data-testid="calendar-next" onClick={() => shift(1)} className="border border-gold/30 font-condensed uppercase tracking-widest text-sm px-4 py-2 hover:border-gold hover:text-gold transition-colors">Next &rarr;</button>
      </div>
      <div className="grid grid-cols-7 gap-px bg-gold/10 border border-gold/15">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="bg-obsidian px-2 py-2.5 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{d}</div>
        ))}
        {cells.map((d, i) => (
          <div key={i} className={`min-h-[90px] sm:min-h-[110px] p-2 ${d ? "bg-obsidian/60" : "bg-obsidian"}`} data-testid={d ? `calendar-day-${key(d)}` : undefined}>
            {d && (
              <>
                <span className={`font-mono text-xs ${isToday(d) ? "text-obsidian bg-gold px-1.5 py-0.5 font-semibold" : "text-stone-500"}`}>{d}</span>
                <div className="mt-1.5 space-y-1">
                  {(byDate[key(d)] || []).map((ev) => (
                    <div key={ev.id} className="border-l-2 border-gold bg-card2/80 px-1.5 py-1" data-testid={`calendar-event-${ev.id}`}>
                      <p className="font-condensed uppercase tracking-wide text-[11px] text-parchment leading-tight">{ev.title}</p>
                      <p className="font-mono text-[9px] text-crimson uppercase tracking-widest">{ev.type}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Directory({ members }) {
  const [q, setQ] = useState("");
  const filtered = members.filter((m) =>
    `${m.name} ${m.callsign} ${m.specialty} ${m.tier}`.toLowerCase().includes(q.toLowerCase())
  );
  return (
    <div data-testid="member-directory">
      <div className="relative max-w-md mb-8">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
        <Input
          data-testid="directory-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, callsign, or specialty..."
          className="pl-10 bg-obsidian border-gold/25 text-parchment focus-visible:ring-gold rounded-none"
        />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((m) => (
          <div key={m.id} className="h-full border border-gold/15 bg-card2/50 p-6 hover:border-crimson/40 transition-colors duration-300" data-testid={`member-card-${m.id}`}>
            <div className="flex items-start justify-between">
              {m.avatar_path ? (
                <img src={`${FILES_BASE}${m.avatar_path}`} alt={m.name} className="w-10 h-10 object-cover rounded-full border border-gold/40" />
              ) : (
                <LogoMark className="w-8 h-8 text-gold/60" />
              )}
              <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${m.role === "admin" ? "bg-crimson/20 text-crimson border border-crimson/40" : "bg-gold/10 text-gold border border-gold/30"}`}>
                {m.tier}
              </span>
            </div>
            <h3 className="font-condensed uppercase tracking-wider text-xl text-parchment mt-4">{m.name}</h3>
            {m.callsign && <p className="font-mono text-xs text-gold tracking-[0.2em] mt-0.5 uppercase">"{m.callsign}"</p>}
            <p className="text-sm text-stone-400 mt-2">{m.specialty || "Member in good standing"}</p>
            {m.phone && <p className="font-mono text-xs text-stone-500 mt-1.5 tracking-wider">{m.phone}</p>}
          </div>
        ))}
        {filtered.length === 0 && <p className="text-stone-500 text-sm col-span-full py-8" data-testid="directory-empty">No members match "{q}".</p>}
      </div>
    </div>
  );
}

function Announcements({ announcements }) {
  return (
    <div className="max-w-3xl space-y-3" data-testid="announcements-list">
      {announcements.map((a) => (
        <div key={a.id} className="border border-gold/15 bg-card2/50 px-7 py-6" data-testid={`announcement-${a.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-obsidian bg-gold px-2 py-0.5 font-semibold">{a.tag}</span>
            <span className="font-mono text-[11px] text-stone-500 tracking-widest">{new Date(a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
          </div>
          <h3 className="font-condensed uppercase tracking-wider text-xl text-parchment mt-3">{a.title}</h3>
          <p className="mt-2 text-sm text-stone-300 leading-relaxed">{a.body}</p>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-gold/70">— {a.author}</p>
        </div>
      ))}
    </div>
  );
}

export default function Members() {
  const { user } = useAuth();
  const [data, setData] = useState({ events: [], announcements: [], resources: [], members: [], orders: [] });
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([
      api.get("/events"),
      api.get("/announcements"),
      api.get("/resources"),
      api.get("/members"),
      api.get("/orders/my").catch(() => ({ data: [] })),
    ]).then(([ev, an, re, me, or]) => {
      setData({ events: ev.data, announcements: an.data, resources: re.data, members: me.data, orders: or.data });
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { if (user && user.approved !== false) load(); }, [user]);

  const payDues = async (lookupKey) => {
    try {
      const { data } = await api.post("/payments/checkout", { kind: "dues", lookup_key: lookupKey, origin_url: window.location.origin });
      toast.success("Redirecting to secure checkout...");
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const onInvite = async (eventId) => {
    try {
      const { data: gp } = await api.post("/guest-passes", { event_id: eventId });
      const link = `${window.location.origin}/guest/${gp.code}`;
      try { await navigator.clipboard.writeText(link); } catch {}
      toast.success("Guest pass created — link copied", { description: link });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const onRsvp = async (eventId) => {
    try {
      const { data: res } = await api.post(`/events/${eventId}/rsvp`);
      setData((d) => ({
        ...d,
        events: d.events.map((e) =>
          e.id === eventId
            ? { ...e, rsvps: res.rsvped ? [...e.rsvps, user.id] : e.rsvps.filter((id) => id !== user.id) }
            : e
        ),
      }));
      toast.success(res.rsvped ? "RSVP confirmed. See you on the line." : "RSVP withdrawn.");
    } catch {
      toast.error("Could not update RSVP");
    }
  };

  if (user === null) {
    return <main className="pt-16 min-h-screen flex items-center justify-center"><p className="font-mono text-sm text-gold tracking-[0.3em] uppercase animate-pulse" data-testid="members-loading">Verifying credentials...</p></main>;
  }
  if (user === false) return <Navigate to="/login" replace />;

  if (user.approved === false) {
    return (
      <main className="relative overflow-hidden pt-16 min-h-screen flex items-center justify-center noise-overlay crosshair-grid px-5" data-testid="members-pending">
        <div className="w-full max-w-md border border-gold/25 bg-card2/60 backdrop-blur p-8 sm:p-10 my-16 text-center">
          <LogoMark className="w-14 h-14 text-gold mx-auto" />
          <h1 className="font-display text-2xl uppercase tracking-wider text-parchment mt-5">Awaiting the Order's Approval</h1>
          <p className="mt-4 text-sm text-stone-400 leading-relaxed">
            Your account is ready, {user.name.split(" ")[0]}. An officer will review your membership — once approved, the Members HQ opens to you.
          </p>
          <p className="mt-4 text-sm text-stone-400">
            In the meantime, the <Link to="/store" data-testid="pending-store-link" className="text-gold underline underline-offset-4">armory</Link> and <Link to="/devotions" data-testid="pending-devotions-link" className="text-gold underline underline-offset-4">devotions</Link> are open to all.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-16 min-h-screen" data-testid="members-page">
      <section className="relative overflow-hidden border-b border-gold/15 noise-overlay crosshair-grid">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 flex flex-wrap items-center justify-between gap-6">
          <div>
            <p className="eyebrow mb-3">Members HQ — Authorized Only</p>
            <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-parchment">
              Welcome, <span className="text-gold">{user.name.split(" ")[0]}</span>
            </h1>
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone-500 border border-gold/20 px-4 py-3">
            {user.tier} &bull; {user.role === "admin" ? "Officer" : "Member"}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        {loading ? (
          <div className="space-y-4" data-testid="members-content-loading">
            {[...Array(3)].map((_, i) => <div key={i} className="h-28 bg-card2/50 border border-gold/10 animate-pulse" />)}
          </div>
        ) : (
          <Tabs defaultValue="overview" data-testid="members-tabs">
            <TabsList className="bg-transparent border border-gold/20 rounded-none h-auto p-1 flex flex-wrap">
              {[
                { v: "overview", label: "Overview", icon: LayoutDashboard },
                { v: "training", label: "Training", icon: BookOpen },
                { v: "events", label: "Events", icon: CalendarDays },
                { v: "calendar", label: "Calendar", icon: CalendarDays },
                { v: "directory", label: "Directory", icon: Users },
                { v: "announcements", label: "Announcements", icon: Megaphone },
              ].map((t) => (
                <TabsTrigger
                  key={t.v}
                  value={t.v}
                  data-testid={`tab-${t.v}`}
                  className="rounded-none font-condensed uppercase tracking-[0.15em] text-sm px-4 py-2.5 data-[state=active]:bg-gold data-[state=active]:text-obsidian data-[state=active]:font-semibold text-stone-400"
                >
                  <t.icon className="w-4 h-4 mr-2 hidden sm:inline" />{t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <div className="mt-10">
              <TabsContent value="overview"><Overview user={user} events={data.events} announcements={data.announcements} orders={data.orders} onPayDues={payDues} /></TabsContent>
              <TabsContent value="training"><Resources resources={data.resources} /></TabsContent>
              <TabsContent value="events"><EventsTab events={data.events} userId={user.id} onRsvp={onRsvp} onInvite={onInvite} /></TabsContent>
              <TabsContent value="calendar"><CalendarTab events={data.events} /></TabsContent>
              <TabsContent value="directory"><Directory members={data.members} /></TabsContent>
              <TabsContent value="announcements"><Announcements announcements={data.announcements} /></TabsContent>
            </div>
          </Tabs>
        )}
      </section>
    </main>
  );
}
